import React, { useState, useEffect } from 'react';
import { userService, agencyService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Users as UsersIcon, UserCheck, UserX, KeyRound, Edit, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const roleLabels = {
  SUPER_ADMIN: { label: 'Super Admin', color: 'bg-purple-100 text-purple-800' },
  AGENCY_ADMIN: { label: 'Admin Agencia', color: 'bg-blue-100 text-blue-800' },
  SALES: { label: 'Ventas', color: 'bg-green-100 text-green-800' },
  EDITOR: { label: 'Editor', color: 'bg-yellow-100 text-yellow-800' },
  VIEWER: { label: 'Visor', color: 'bg-gray-100 text-gray-800' }
};

const statusLabels = {
  active: { label: 'Activo', variant: 'default' },
  suspended: { label: 'Suspendido', variant: 'secondary' }
};

const AdminUsersPage = () => {
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAgency, setFilterAgency] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    profile: {
      fullName: '',
      email: '',
      phone: ''
    },
    password: '',
    role: 'AGENCY_ADMIN',
    agencyId: ''
  });

  const [editData, setEditData] = useState({
    profile: {
      fullName: '',
      email: '',
      phone: ''
    },
    role: '',
    status: '',
    agencyId: ''
  });

  useEffect(() => {
    loadData();
  }, [filterAgency]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = filterAgency !== 'all' ? { agencyId: filterAgency } : {};
      const [usersData, agenciesData] = await Promise.all([
        userService.getAll(params),
        isSuperAdmin ? agencyService.getAll() : Promise.resolve([])
      ]);
      setUsers(usersData);
      setAgencies(agenciesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await userService.create(formData);
      toast.success('Usuario creado');
      setShowCreateDialog(false);
      setFormData({
        profile: { fullName: '', email: '', phone: '' },
        password: '',
        role: 'AGENCY_ADMIN',
        agencyId: ''
      });
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al crear usuario');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await userService.update(selectedUser.id, editData);
      toast.success('Usuario actualizado');
      setShowEditDialog(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al actualizar');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditData({
      profile: {
        fullName: user.profile?.fullName || '',
        email: user.profile?.email || '',
        phone: user.profile?.phone || ''
      },
      role: user.role,
      status: user.status,
      agencyId: user.agencyId || ''
    });
    setShowEditDialog(true);
  };

  const openDetailDialog = (user) => {
    setSelectedUser(user);
    setShowDetailDialog(true);
  };

  const toggleStatus = async (user) => {
    try {
      const newStatus = user.status === 'active' ? 'suspended' : 'active';
      await userService.update(user.id, { status: newStatus });
      toast.success('Estado actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    try {
      await userService.resetPassword(selectedUser.id, newPassword);
      toast.success('Contraseña actualizada');
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Error al cambiar contraseña');
    }
  };

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency ? agency.name : 'Sin agencia';
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900">Usuarios</h1>
          <p className="text-slate-600 mt-2">Gestiona usuarios del sistema</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-button">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Crear Usuario</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Nombre Completo *</label>
                <Input
                  value={formData.profile.fullName}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    profile: { ...formData.profile, fullName: e.target.value }
                  })}
                  placeholder="Juan Pérez"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Email *</label>
                <Input
                  type="email"
                  value={formData.profile.email}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    profile: { ...formData.profile, email: e.target.value }
                  })}
                  placeholder="juan@agencia.com"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Teléfono</label>
                <Input
                  value={formData.profile.phone}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    profile: { ...formData.profile, phone: e.target.value }
                  })}
                  placeholder="+54 9 261 555 1234"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Contraseña *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Rol *</label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperAdmin && (
                      <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                    )}
                    <SelectItem value="AGENCY_ADMIN">Admin de Agencia</SelectItem>
                    <SelectItem value="SALES">Ventas</SelectItem>
                    <SelectItem value="EDITOR">Editor</SelectItem>
                    <SelectItem value="VIEWER">Visor (Solo lectura)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.role !== 'SUPER_ADMIN' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Agencia *</label>
                  <Select 
                    value={formData.agencyId} 
                    onValueChange={(value) => setFormData({ ...formData, agencyId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar agencia" />
                    </SelectTrigger>
                    <SelectContent>
                      {agencies.map(agency => (
                        <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button type="submit" className="w-full">Crear Usuario</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isSuperAdmin && agencies.length > 0 && (
        <div className="mb-6">
          <Select value={filterAgency} onValueChange={setFilterAgency}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Filtrar por agencia" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las agencias</SelectItem>
              {agencies.map(agency => (
                <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UsersIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 mb-4">No hay usuarios registrados</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {users.map((user) => {
            const roleInfo = roleLabels[user.role] || roleLabels.VIEWER;
            const statusInfo = statusLabels[user.status] || statusLabels.active;
            const profile = user.profile || {};
            
            return (
              <Card key={user.id} data-testid={`user-card-${user.id}`}>
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-bold text-lg">{profile.fullName}</h3>
                        <Badge variant={statusInfo.variant}>
                          {statusInfo.label}
                        </Badge>
                        <Badge className={roleInfo.color}>
                          {roleInfo.label}
                        </Badge>
                      </div>
                      <p className="text-slate-600 text-sm">{profile.email}</p>
                      {profile.phone && (
                        <p className="text-slate-500 text-sm">Tel: {profile.phone}</p>
                      )}
                      {user.agencyId && (
                        <p className="text-slate-500 text-sm mt-1">
                          Agencia: <span className="font-medium">{getAgencyName(user.agencyId)}</span>
                        </p>
                      )}
                      {!user.agencyId && user.role === 'SUPER_ADMIN' && (
                        <p className="text-purple-600 text-sm mt-1 font-medium">
                          Acceso global al sistema
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDetailDialog(user)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(user)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordDialog(true);
                        }}
                      >
                        <KeyRound className="w-4 h-4 mr-1" />
                        Clave
                      </Button>
                      <Button
                        variant={user.status === 'active' ? 'destructive' : 'default'}
                        size="sm"
                        onClick={() => toggleStatus(user)}
                      >
                        {user.status === 'active' ? (
                          <><UserX className="w-4 h-4 mr-1" /> Suspender</>
                        ) : (
                          <><UserCheck className="w-4 h-4 mr-1" /> Activar</>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Nombre Completo</label>
              <Input
                value={editData.profile.fullName}
                onChange={(e) => setEditData({ 
                  ...editData, 
                  profile: { ...editData.profile, fullName: e.target.value }
                })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Email</label>
              <Input
                type="email"
                value={editData.profile.email}
                onChange={(e) => setEditData({ 
                  ...editData, 
                  profile: { ...editData.profile, email: e.target.value }
                })}
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Teléfono</label>
              <Input
                value={editData.profile.phone || ''}
                onChange={(e) => setEditData({ 
                  ...editData, 
                  profile: { ...editData.profile, phone: e.target.value }
                })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Rol</label>
              <Select 
                value={editData.role} 
                onValueChange={(value) => setEditData({ ...editData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin && (
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  )}
                  <SelectItem value="AGENCY_ADMIN">Admin de Agencia</SelectItem>
                  <SelectItem value="SALES">Ventas</SelectItem>
                  <SelectItem value="EDITOR">Editor</SelectItem>
                  <SelectItem value="VIEWER">Visor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Estado</label>
              <Select 
                value={editData.status} 
                onValueChange={(value) => setEditData({ ...editData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="suspended">Suspendido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isSuperAdmin && editData.role !== 'SUPER_ADMIN' && (
              <div>
                <label className="text-sm font-medium mb-2 block">Agencia</label>
                <Select 
                  value={editData.agencyId || 'none'} 
                  onValueChange={(value) => setEditData({ ...editData, agencyId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar agencia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin agencia asignada</SelectItem>
                    {agencies.map(agency => (
                      <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit">Guardar Cambios</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalles del Usuario</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Nombre</p>
                  <p className="font-medium">{selectedUser.profile?.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium">{selectedUser.profile?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Teléfono</p>
                  <p className="font-medium">{selectedUser.profile?.phone || 'No registrado'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Rol</p>
                  <Badge className={roleLabels[selectedUser.role]?.color}>
                    {roleLabels[selectedUser.role]?.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Estado</p>
                  <Badge variant={statusLabels[selectedUser.status]?.variant}>
                    {statusLabels[selectedUser.status]?.label}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Agencia</p>
                  <p className="font-medium">
                    {selectedUser.agencyId ? getAgencyName(selectedUser.agencyId) : 'Global (Sin agencia)'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">ID</p>
                  <p className="font-mono text-xs">{selectedUser.id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Creado</p>
                  <p className="font-medium text-sm">
                    {new Date(selectedUser.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-slate-500 mb-2">Contraseña</p>
                <p className="text-xs text-slate-400 bg-slate-50 p-2 rounded">
                  Las contraseñas están encriptadas y no pueden ser visualizadas. 
                  Usa el botón "Clave" para resetear la contraseña del usuario.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resetear Contraseña</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Nueva contraseña para: <strong>{selectedUser?.profile?.fullName}</strong>
            </p>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva contraseña (mín. 6 caracteres)"
                minLength={6}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => {
                setShowPasswordDialog(false);
                setNewPassword('');
              }}>
                Cancelar
              </Button>
              <Button onClick={handleResetPassword} disabled={newPassword.length < 6}>
                Cambiar Contraseña
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
